import { useState } from "react";
import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import { convertLength } from "@/utils";
import { DemographicLimits } from "@/features/HealthCheck/types";
import {
  BMI_HEIGHT_MIN_CM,
  BMI_HEIGHT_MAX_CM,
} from "@/features/HealthScore/utils/submitHealthProfileEntry";
import VitalManualEntryLayout from "../../../Common/VitalManualEntryLayout";
import {
  SingleVitalInput,
  VitalInputWithUnitSelect,
} from "../../../Common/VitalInput";

export interface BMIUpdateFormProps {
  onBack: () => void;
  onSave: (weightKg: number, heightCm: number, heightUnit: "cm" | "m") => void;
}

export default function BMIUpdateForm({ onBack, onSave }: BMIUpdateFormProps) {
  const { t } = useTranslation();
  const [weight, setWeight] = useState("");
  const [heightUnit, setHeightUnit] = useState<"cm" | "m">("cm");
  const [height, setHeight] = useState("");

  const weightNum = Number(weight);
  const heightNum = Number(height);
  const heightCm =
    heightUnit === "cm"
      ? heightNum
      : Math.round(convertLength("M", "CM", heightNum));
  const isValid =
    weightNum >= DemographicLimits.MIN_ALLOWED_WEIGHT &&
    weightNum <= DemographicLimits.MAX_ALLOWED_WEIGHT &&
    ((heightUnit === "cm" &&
      heightNum >= BMI_HEIGHT_MIN_CM &&
      heightNum <= BMI_HEIGHT_MAX_CM) ||
      (heightUnit === "m" &&
        heightNum >= BMI_HEIGHT_MIN_CM / 100 &&
        heightNum <= BMI_HEIGHT_MAX_CM / 100));

  const handleHeightUnitChange = (unit: "cm" | "m") => {
    if (height && unit !== heightUnit) {
      const num = Number(height);
      const newValue =
        unit === "m"
          ? convertLength("CM", "M", num)
          : Math.round(convertLength("M", "CM", num));
      setHeight(unit === "m" ? String(newValue.toFixed(2)) : String(newValue));
    }
    setHeightUnit(unit);
  };

  const handleSave = () => {
    if (isValid) {
      onSave(weightNum, heightCm, heightUnit);
    }
  };

  const weightPlaceholder = t(
    "HealthHistory.bmi.updateModal.weightPlaceholder"
  );
  const heightPlaceholder =
    heightUnit === "cm"
      ? t("HealthHistory.bmi.updateModal.heightPlaceholder")
      : t("HealthHistory.bmi.updateModal.heightPlaceholderM");

  return (
    <VitalManualEntryLayout
      title={t("HealthHistory.bmi.updateModal.title")}
      onBack={onBack}
      onCancel={onBack}
      onSave={handleSave}
      isSaveDisabled={!isValid}
      cancelText={t("HealthHistory.bmi.updateModal.cancel")}
      saveText={t("HealthHistory.bmi.updateModal.save")}
    >
      <Stack spacing={3} sx={{ width: "100%" }}>
        <SingleVitalInput
          value={weight}
          onChange={setWeight}
          placeholder={weightPlaceholder}
          ariaLabel={t("HealthHistory.bmi.weight")}
          unit="KG"
          min={DemographicLimits.MIN_ALLOWED_WEIGHT}
          max={DemographicLimits.MAX_ALLOWED_WEIGHT}
          fullWidth
          errorText={t(
            "HealthHistory.bmi.weightValidationError",
            "Weight must be between 40 and 200 kg"
          )}
        />
        <VitalInputWithUnitSelect
          value={height}
          onChange={setHeight}
          unit={heightUnit}
          onUnitChange={handleHeightUnitChange}
          placeholder={heightPlaceholder}
          ariaLabel={t("HealthHistory.bmi.height")}
          min={BMI_HEIGHT_MIN_CM}
          max={BMI_HEIGHT_MAX_CM}
          errorText={
            heightUnit === "cm"
              ? t(
                  "HealthHistory.bmi.heightValidationError",
                  "Height must be between 100 and 250 cm"
                )
              : t(
                  "HealthHistory.bmi.heightValidationErrorM",
                  "Height must be between 1.00 and 2.50 m"
                )
          }
        />
      </Stack>
    </VitalManualEntryLayout>
  );
}
